/**
 * Input Component - Theme-aware input field
 * Pattern based on github-admin styles
 */

const React = (globalThis as any).React;
const { useState, forwardRef } = React;

import { styles } from '../styles';

export interface InputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'password' | 'number' | 'email' | 'search';
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
  style?: React.CSSProperties;
  'aria-label'?: string;
  'aria-describedby'?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  id?: string;
  // Filter out unsupported props that might come from AutoComplete or other wrappers
  allowClear?: any;
  onPressEnter?: any;
  onSearch?: any;
  filterOption?: any;
  open?: any;
  options?: any;
  size?: any;
  // Explicitly do NOT accept children - input is a void element
  children?: never;
}

// ✅ Fix: Use forwardRef properly and ensure no invalid props reach the DOM
// forwardRef expects (props, ref) => ReactElement
const InputComponent = (props: any, ref: any) => {
  // Extract only valid input props - filter out everything else
  // CRITICAL: Do NOT accept children - input is a void element
  const {
    value,
    onChange,
    placeholder,
    type = 'text',
    disabled = false,
    error = false,
    errorMessage,
    required = false,
    style,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    onKeyDown,
    prefix,
    suffix,
    id,
    // Explicitly ignore these props (they come from AutoComplete or other wrappers)
    // These MUST be destructured and NOT passed to the DOM
    allowClear: _allowClear,
    onPressEnter: _onPressEnter,
    onSearch: _onSearch,
    filterOption: _filterOption,
    open: _open,
    options: _options,
    size: _size,
    children: _children, // Explicitly ignore children - input is void
    // CRITICAL: Ignore restProps completely - they may contain children or invalid props
    // Do NOT spread ...restProps anywhere
  } = props;
  
  // Ensure we never pass children or invalid props to the native input
  // Input is a void element and cannot have children
  const [isFocused, setIsFocused] = useState(false);
  
  const inputStyle: React.CSSProperties = {
    ...styles.input,
    ...(isFocused ? styles.inputFocus : {}),
    ...(error ? { borderColor: 'var(--error-color)' } : {}),
    ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
    ...(prefix ? { paddingLeft: '40px' } : {}),
    ...(suffix ? { paddingRight: '40px' } : {}),
    ...style,
  };
  
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: style?.width || '100%',
  };
  
  // Build clean input props object - ONLY valid HTML input attributes
  // CRITICAL: Do NOT include any AutoComplete-specific props, children, or invalid DOM attributes
  // Only include props that are valid for a native <input> element
  const inputProps: Record<string, any> = {
    ref,
    id: id || undefined,
    type: type || 'text',
    value: value || '',
    onChange: (e: any) => onChange?.(e.target.value),
    placeholder: placeholder || undefined,
    disabled: disabled || undefined,
    required: required || undefined,
    style: inputStyle,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onKeyDown: (e: any) => {
      // Handle onPressEnter if provided (from AutoComplete) - but don't pass it as a prop
      if (e.key === 'Enter' && _onPressEnter) {
        _onPressEnter();
      }
      onKeyDown?.(e);
    },
  };
  
  // Only add valid ARIA attributes if they have values
  // CRITICAL: Do NOT add allowClear, onPressEnter, onSearch, filterOption, etc. - these are NOT valid DOM attributes
  if (ariaLabel) inputProps['aria-label'] = ariaLabel;
  if (errorMessage && id) inputProps['aria-describedby'] = `${id}-error`;
  else if (ariaDescribedBy) inputProps['aria-describedby'] = ariaDescribedBy;
  if (required) inputProps['aria-required'] = required;
  if (error) inputProps['aria-invalid'] = error;
  
  if (prefix || suffix) {
    return React.createElement('div', { style: containerStyle },
      prefix && React.createElement('span', {
        style: {
          position: 'absolute',
          left: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--main-text-secondary)',
          pointerEvents: 'none',
        },
      }, prefix),
      React.createElement('input', inputProps),
      errorMessage && React.createElement('div', {
        id: `${id}-error`,
        role: 'alert',
        style: {
          marginTop: '6px',
          fontSize: '13px',
          color: 'var(--error-color)',
        },
      }, errorMessage),
      suffix && React.createElement('span', {
        style: {
          position: 'absolute',
          right: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--main-text-secondary)',
          pointerEvents: 'none',
        },
      }, suffix)
    );
  }
  
  // ✅ Fix: Use the clean inputProps object we built above (no invalid props, no children)
  return React.createElement('div', { style: { width: '100%' } },
    React.createElement('input', inputProps),
    errorMessage && React.createElement('div', {
      id: `${id}-error`,
      role: 'alert',
      style: {
        marginTop: '6px',
        fontSize: '13px',
        color: 'var(--error-color)',
      },
    }, errorMessage)
  );
};

// ✅ Fix: Use forwardRef properly with explicit typing
export const Input = (forwardRef as any)(InputComponent) as React.ForwardRefExoticComponent<InputProps & React.RefAttributes<HTMLInputElement>>;

